import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Filter Persistence Store
 *
 * Handles localStorage persistence for list page filters.
 * URL remains the source of truth for current filter state,
 * this store just remembers filters for when users return to pages.
 */

// Generic filter type - each page can have different filter shapes
// Using 'object' to allow any filter shape without requiring index signature
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFilters = Record<string, any>

interface FilterState {
  // Stored filters by page key (e.g., 'assistants', 'conversations', 'notes')
  filters: Record<string, AnyFilters>

  // Get filters for a specific page
  getFilters: (key: string) => AnyFilters | null

  // Save filters for a specific page
  setFilters: (key: string, filters: AnyFilters) => void

  // Clear filters for a specific page
  clearFilters: (key: string) => void

  // Clear all filters (useful for logout)
  clearAllFilters: () => void
}

export const useFilterStore = create<FilterState>()(
  persist(
    (set, get) => ({
      filters: {},

      getFilters: (key) => {
        const filters = get().filters[key]
        // Return null if empty or doesn't exist
        if (!filters || Object.keys(filters).length === 0) {
          return null
        }
        return filters
      },

      setFilters: (key, filters) => {
        // Clean filters: remove undefined, null, empty strings, and empty arrays
        const cleanFilters = Object.fromEntries(
          Object.entries(filters).filter(([, v]) => {
            if (v === undefined || v === null || v === '') return false
            if (Array.isArray(v) && v.length === 0) return false
            return true
          })
        )

        set((state) => {
          // If clean filters is empty, remove the key entirely
          if (Object.keys(cleanFilters).length === 0) {
            const { [key]: _, ...rest } = state.filters
            return { filters: rest }
          }
          return {
            filters: { ...state.filters, [key]: cleanFilters },
          }
        })
      },

      clearFilters: (key) => {
        set((state) => {
          const { [key]: _, ...rest } = state.filters
          return { filters: rest }
        })
      },

      clearAllFilters: () => {
        set({ filters: {} })
      },
    }),
    {
      name: 'bonnie-filters',
    }
  )
)

export default useFilterStore
